/**
 * La phrase d'ÉCRAN d'un refus du module « Appels d'offres », choisie par son CODE.
 *
 * ## Le défaut
 *
 * `handleTenderError` rend la phrase du SERVEUR (`detail.message`) : elle est
 * rédigée en français. Pour la plupart des refus, c'est ce qu'il faut — elle
 * nomme la pièce qui manque, le motif exigé, le nombre qui dépasse, et l'écran
 * n'a rien de mieux à dire. Mais quand la phrase ne porte AUCUNE donnée propre à
 * la requête, la garder revient à afficher du français dans l'interface anglaise
 * pour rien : l'écran peut la dire lui-même, dans la langue de l'utilisateur.
 *
 * C'est le cas du 410 `TENDER_ANONYMIZED` (décision PO du 2026-09-22, « lecture
 * seule, sauf supprimer »), que toute écriture d'une consultation anonymisée
 * reçoit — `content_lock.assert_not_anonymized`, `_helpers.modifiable`. L'écran
 * n'offre plus ces gestes sur une fiche qu'il SAIT anonymisée : le refus n'arrive
 * donc plus que d'un écran périmé (une fiche anonymisée entre deux lectures), ou
 * d'une porte qui l'ignore encore. C'est précisément là qu'il doit se lire.
 *
 * ## Ce que la phrase dit
 *
 * Ce que dit le serveur (`PHRASE_ANONYMISEE`) : ce qui reste possible (lire,
 * retirer), ce qui ne l'est plus, que les lots et leurs prix restent tels quels,
 * et que rien ne se lève. ⚠️ Ni « bordereau » ni « ligne » — la même précaution
 * que le serveur, pour la même raison historique (l'onglet Lots lisait ces mots).
 *
 * ## Pourquoi `i18next` et pas `useTranslation`
 *
 * Le refus se forme DANS la requête, hors de tout composant : pas de crochet ici.
 * On lit l'instance globale, que `@/i18n` initialise au démarrage — c'est donc la
 * langue de l'écran au moment du refus. Hors application (un test qui ne
 * l'initialise pas), `i18next.t` ne rend rien : on retombe sur la phrase française
 * écrite ici, jamais sur la clé brute.
 */

import i18next from 'i18next'

import type { TenderProblem } from './useTenders.types'

/** Le code métier du refus : `detail.code` (forme `rfc7807_error`), sinon à plat. */
export function codeDuRefusTender(payload: TenderProblem): string | null {
    const detail: unknown = payload.detail
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const code = (detail as { code?: unknown }).code
        if (typeof code === 'string') return code
    }
    return typeof payload.code === 'string' ? payload.code : null
}

export const ANONYMISEE_FR =
    'Cette consultation est anonymisée : elle se consulte, elle ne se modifie plus. On peut encore en retirer un document, une pièce à fournir, une échéance, un échange en brouillon ou un membre de l’équipe ; ses lots et leurs prix restent tels quels, pour les statistiques. L’anonymisation ne se défait pas.'

/**
 * La phrase que l'écran dit À LA PLACE de celle du serveur, ou `null` : alors la
 * phrase du serveur reste la bonne (elle porte une donnée que l'écran n'a pas).
 */
export function phraseDEcranDuRefus(payload: TenderProblem): string | null {
    switch (codeDuRefusTender(payload)) {
        case 'TENDER_ANONYMIZED':
            return (
                i18next.t('sales.tenders.errors.TENDER_ANONYMIZED', {
                    defaultValue: ANONYMISEE_FR,
                }) || ANONYMISEE_FR
            )
        default:
            return null
    }
}
